import React, {useMemo} from "react";
import moment from "moment";

import {
	Bar,
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

import EmptyData from "@/components/EmptyData";

import "./index.scss";

function ChartContainer(props) {
	const {type, title, data, dataKey, chartItems, range = false} = props;

	const formatDate = (date) => moment(date).format("DD/MM/YYYY");

	let maximumValue = 0;
	let minimumValue = null;

	const readyData = [];
	data.forEach((d) => {
		const element = Object.assign({}, d);
		chartItems?.forEach((i) => {
			if (element[i.value] > maximumValue) {
				maximumValue = element[i.value];
			}

			if (minimumValue === null || element[i.value] < minimumValue) {
				minimumValue = element[i.value];
			}

			if (element[i.value] === 0) {
				element[i.value] = null;
			}
		});
		readyData.push(element);
	});

	const generateTicks = (min, max, steps = 5) => {
		const range = max - min;
		const step = Math.ceil(range / (steps - 1));
		const base = Math.floor(min);
		return Array.from({length: steps}, (_, i) => base + i * step);
	};

	const xTicks = useMemo(() => {
		return data.map((d) => new Date(d[dataKey]).getTime());
	}, [data, dataKey]);

	const yTicks = useMemo(() => {
		return generateTicks(
			range ? minimumValue - 50 : 0,
			range ? maximumValue + 30 : maximumValue + 5,
		);
	}, [minimumValue, maximumValue]);

	const getChart = () => {
		const dataWithTimestamps = readyData.map((d) => ({
			...d,
			dateTimestamp: new Date(d[dataKey]).getTime(),
		}));

		switch (type) {
			case "line":
				return (
					<ResponsiveContainer>
						<LineChart data={range ? dataWithTimestamps : readyData}>
							{chartItems.map((ck) => {
								return (
									<Line
										key={ck.value}
										type="monotone"
										dataKey={ck.value}
										stroke={ck.color}
										name={ck.label}
										dot={{r: 3}}
									/>
								);
							})}
							<CartesianGrid stroke="#ccc" />
							<XAxis
								type={range ? "number" : undefined}
								dataKey={range ? "dateTimestamp" : dataKey}
								domain={["dataMin", "dataMax"]}
								tickFormatter={dataKey === "date" ? formatDate : undefined}
								ticks={range ? xTicks : undefined}
							/>
							<YAxis
								domain={[
									range ? minimumValue - 50 : 0,
									range ? maximumValue + 30 : maximumValue + 5,
								]}
								ticks={yTicks}
							/>
							<Tooltip />
						</LineChart>
					</ResponsiveContainer>
				);
			default:
				return <></>;
		}
	};

	return (
		<div className="chart-container">
			{title && <div className="chart-title">{title}</div>}
			{data?.length ? getChart() : <EmptyData />}
		</div>
	);
}

export default ChartContainer;
